import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { PATIENT_SERVICES } from '@avanta/shared/constants/patients';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { useNavRedirect } from 'hooks/useNavRedirect';
import { useUserStore } from 'stores/user';
import { QuestionHeader } from './components/QuestionHeader';
import SapaMessageItem from './components/SapaMessageItem';
import { SapaChatProvider, useSapaChatContext } from './context';

const SapaChatContent = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.SERVICES);
  const navigate = useNavigate();
  const { messages, isProcessing, isFinished } = useSapaChatContext();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const patient = useUserStore(state => state.user);

  useNavRedirect(!!patient && !patient.activeServices?.includes(PATIENT_SERVICES.SAPA));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, messagesEndRef]);

  if (!patient) {
    return <LoadingScreen />;
  }

  return (
    <div className="absolute inset-x-0 top-16 bottom-0 w-full">
      <div className="flex flex-col h-full">
        <QuestionHeader />
        <div className="flex-1 p-4 md:p-6 md:px-12 bg-gray-50 w-full overflow-y-auto">
          {messages.map(message => (
            <SapaMessageItem key={message.id} message={message} />
          ))}
          {isProcessing && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="h-6 w-6 text-teal-600 animate-spin" />
            </div>
          )}
          {isFinished && (
            <div className="flex justify-center pt-4">
              <Button variant="terciary" onClick={() => navigate(ROUTES.HOME)}>
                {t('Sapa.goToHome.cta')}
              </Button>
            </div>
          )}
          <div className="invisible h-1" ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

export const SapaChat = (): React.ReactElement => (
  <SapaChatProvider>
    <SapaChatContent />
  </SapaChatProvider>
);
